import { create } from "zustand";
import { User, Session, AuthWeakPasswordError } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

/*
Primary auth store - consolidating from authContext
*/
interface Profile {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  preferences: JSON;
  privacy_settings: JSON;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  initialized: boolean;
  justSignedUp: boolean; // New flag to track fresh signups
  isRecoverySession: boolean;
  signUp: (
    email: string,
    password: string,
    username: string | null,
    fullName: string,
  ) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  initialize: () => Promise<void>;
  clearJustSignedUp: () => void; // Method to clear the signup flag
  clearRecoverySession: () => void;
  checkUsernameAvailable: (username: string) => Promise<boolean>;
  isWaitlisted: () => boolean;
  isOnboardingComplete: () => boolean;
  isPasswordSet: () => boolean;
  resetPasswordForEmail: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
  approveFromWaitlist: () => Promise<void>;
}

// Module-level subscription reference to prevent duplicate listeners across hot-reloads
let authSubscription: { unsubscribe: () => void } | null = null;

export const useAuthStore = create<AuthState>((set, get) => {
  // Register the auth state change subscription SYNCHRONOUSLY at store creation time.
  // This ensures PASSWORD_RECOVERY is caught before it fires: Supabase schedules the
  // event via setTimeout(0) after processing the URL hash, but our subscription must
  // be in place before that macrotask runs. Registering here (synchronously, at module
  // load) guarantees we're subscribed before any async work can fire the event.
  if (authSubscription) {
    authSubscription.unsubscribe();
  }
  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((event, session) => {
    // IMPORTANT: Do NOT make Supabase client calls (e.g. supabase.from(...))
    // inside this callback. The callback is awaited by the Supabase client's
    // internal initialization, and any client call (like a profile fetch) will
    // call getSession() which awaits the same initialization promise — causing
    // a deadlock that hangs the app on refresh.
    //
    // Profile fetching is handled by initialize() instead.
    if (event === "SIGNED_IN" && session?.user) {
      set({
        user: session.user,
        session,
        loading: false,
        profileLoading: true,
      });
      // Fetch profile outside the callback to avoid deadlock
      supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .maybeSingle()
        .then(({ data: profile }) => {
          if (profile) {
            set({ profile, profileLoading: false });
          } else {
            set({ profileLoading: false });
          }
        });
    } else if (event === "PASSWORD_RECOVERY" && session?.user) {
      set({
        user: session.user,
        session,
        loading: false,
        isRecoverySession: true,
        profileLoading: true,
      });
      // Fetch profile outside the callback to avoid deadlock
      supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .maybeSingle()
        .then(({ data: profile }) => {
          if (profile) {
            set({ profile, profileLoading: false });
          } else {
            set({ profileLoading: false });
          }
        });
    } else if (event === "SIGNED_OUT") {
      set({
        user: null,
        session: null,
        profile: null,
        loading: false,
        justSignedUp: false,
        isRecoverySession: false,
      });
    }
  });
  authSubscription = subscription;

  return {
    user: null,
    session: null,
    profile: null,
    loading: false,
    profileLoading: false,
    initialized: false,
    justSignedUp: false,
    isRecoverySession: false,

    initialize: async () => {
      try {
        set({ loading: true });

        // Validate session server-side — getSession() only reads local storage
        // and won't detect revoked sessions or deleted users
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (user) {
          // getUser() confirmed server-side validity; now retrieve session object from local storage
          const {
            data: { session },
          } = await supabase.auth.getSession();

          // Fetch user profile - use maybeSingle() to handle cases where profile doesn't exist
          const { data: profile } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", user.id)
            .maybeSingle();
          set({
            user,
            session,
            profile,
            initialized: true,
            loading: false,
          });
        } else {
          set({
            user: null,
            session: null,
            profile: null,
            initialized: true,
            loading: false,
          });
        }
        // onAuthStateChange subscription is set up in the factory (above), not here
      } catch (error) {
        console.error("Error initializing auth:", error);
        set({ loading: false, initialized: true });
      }
    },

    signUp: async (
      email: string,
      password: string,
      username: string | null,
      fullName: string,
    ) => {
      try {
        set({ loading: true });

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              username: username,
            },
          },
        });

        if (error) throw error;

        // Profile is created by database trigger using the metadata above
        if (data.user) {
          set({ justSignedUp: true });
        }
      } catch (error) {
        let message = "Authentication error: ";
        if (error instanceof AuthWeakPasswordError) {
          console.error("Weak password error:", error);
          message +=
            "Password should be at least 8 characters long and include a mix of letters, numbers, and symbols.";
        } else {
          console.error(message, error);
          message += "An unexpected error occurred. Please try again.";
        }
        throw new Error(message);
      } finally {
        set({ loading: false });
      }
    },

    signIn: async (email: string, password: string) => {
      try {
        set({ loading: true });

        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        // Don't set justSignedUp for regular sign ins
        set({ justSignedUp: false });
      } catch (error) {
        const message = "Invalid login credentials";
        console.error(message, error);
        throw new Error(message);
      } finally {
        set({ loading: false });
      }
    },

    signOut: async () => {
      try {
        set({ loading: true });
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
      } catch (error) {
        const message = "Failed to sign out";
        console.error(message, error);
        throw new Error(message);
      } finally {
        set({ loading: false });
      }
    },

    updateProfile: async (updates: Partial<Profile>) => {
      try {
        const { user } = get();
        if (!user) throw new Error("No authenticated user");

        set({ loading: true });

        const { data, error } = await supabase
          .from("profiles")
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq("id", user.id)
          .select()
          .single();

        if (error) throw error;

        set({ profile: data });
      } catch (error) {
        const message = "Failed to update profile";
        console.error(message, error);
        throw new Error(message);
      } finally {
        set({ loading: false });
      }
    },

    clearJustSignedUp: () => set({ justSignedUp: false }),

    clearRecoverySession: () => set({ isRecoverySession: false }),

    isWaitlisted: () => {
      const { profile } = get();
      if (!profile) return false;
      const preferences = profile.preferences as unknown as Record<
        string,
        unknown
      > | null;
      // Grandfathering: missing/null preferences or missing key = approved
      if (!preferences || !("waitlist_status" in preferences)) return false;
      return preferences.waitlist_status === "pending";
    },

    isOnboardingComplete: () => {
      const { profile } = get();
      if (!profile) return false;
      const preferences = profile.preferences as unknown as Record<
        string,
        unknown
      > | null;
      // Grandfathering: missing/null preferences or missing key = completed
      if (!preferences || !("onboarding_completed" in preferences)) return true;
      return preferences.onboarding_completed === true;
    },

    isPasswordSet: () => {
      const { profile } = get();
      if (!profile) return true; // No profile = not blocking (unauthenticated)
      const preferences = profile.preferences as unknown as Record<
        string,
        unknown
      > | null;
      // Grandfathering: missing/null preferences or missing key = password is set
      if (!preferences || !("password_set" in preferences)) return true;
      return preferences.password_set === true;
    },

    resetPasswordForEmail: async (email: string) => {
      const siteUrl: string =
        process.env.REACT_APP_SITE_URL || window.location.origin;
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: siteUrl,
      });
      if (error) throw error;
    },

    updatePassword: async (newPassword: string) => {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;

      // Mark password as set for invited users
      const { user, profile } = get();
      if (user) {
        const currentPreferences =
          (profile?.preferences as unknown as Record<string, unknown>) ?? {};
        const updatedPreferences = {
          ...currentPreferences,
          password_set: true,
        };

        const { data } = await supabase
          .from("profiles")
          .update({
            preferences: updatedPreferences,
            updated_at: new Date().toISOString(),
          })
          .eq("id", user.id)
          .select()
          .single();

        if (data) set({ profile: data });
      }
    },

    uploadAvatar: async (file: File) => {
      const { user } = get();
      if (!user) throw new Error("No authenticated user");

      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}/avatar.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("avatars").getPublicUrl(filePath);

      // Append cache-buster since path stays the same with upsert
      const avatarUrl = `${publicUrl}?t=${Date.now()}`;

      const { data, error } = await supabase
        .from("profiles")
        .update({ avatar_url: avatarUrl, updated_at: new Date().toISOString() })
        .eq("id", user.id)
        .select()
        .single();
      if (error) throw error;

      set({ profile: data });
    },

    approveFromWaitlist: async () => {
      const { user, profile } = get();
      if (!user) throw new Error("No authenticated user");

      const currentPreferences =
        (profile?.preferences as unknown as Record<string, unknown>) ?? {};
      const updatedPreferences = {
        ...currentPreferences,
        waitlist_status: "approved",
      };

      const { data, error } = await supabase
        .from("profiles")
        .update({
          preferences: updatedPreferences,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id)
        .select()
        .single();

      if (error) throw error;

      set({ profile: data });
    },

    checkUsernameAvailable: async (username: string): Promise<boolean> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id")
        .eq("username", username.toLowerCase())
        .maybeSingle();

      if (error) throw error;
      return data === null; // Available if no existing profile with that username
    },
  };
});
